export type FieldType = 'string' | 'number' | 'boolean' | 'date' | 'select';

/**
 * Configuration for live search functionality on select fields
 */
export interface SearchConfig {
    /** 
     * Callback function to fetch options from backend.
     * If not provided, 'endpoint' is used instead.
     */
    onSearch?: (searchTerm: string, fieldId: string) => Promise<Record<string, string>>;
    
    /**
     * Search endpoint URL. Used when onSearch is not provided.
     * Query params ?q=term&field=fieldId will be appended.
     */
    endpoint?: string;
    
    /** 
     * Callback to resolve a label for a stored value key.
     * Called when loading existing rules to display the correct label.
     * If not provided, the raw key is displayed as fallback.
     */
    onResolveLabel?: (key: string, fieldId: string) => Promise<string>;
    
    /** Minimum characters required before triggering search (default: 2) */
    minChars?: number;
    
    /** Debounce delay in milliseconds (default: 300) */
    debounceMs?: number;
    
    /** 
     * Search strategy:
     * - 'local': Search only in existing `values` (no backend)
     * - 'remote': Always fetch from backend via `onSearch`
     * - 'hybrid': First search local `values`, fallback to backend if no matches found
     * Default: 'remote'
     */
    mode?: 'local' | 'remote' | 'hybrid';
    
    /**
      * Cache search results to avoid repeated API calls for same search term.
     * - true: Cache indefinitely (default)
     * - false: No caching, always fetch fresh
     * - number: Cache TTL in milliseconds
     */
    cache?: boolean | number;
    
    /**
     * In hybrid mode, always fetch from backend even if local results exist.
     * Local results are shown immediately, then merged with backend results.
     * Default: false
     */
    hybridAlwaysFetch?: boolean;
    
    /**
     * In hybrid mode, minimum local results required to skip backend fetch.
     * If local results count is less than this threshold, backend is called.
     * Default: 1 (any local result skips backend, unless hybridAlwaysFetch=true)
     * Example: Set to 5 to always fetch backend if fewer than 5 local matches.
     */
    hybridFetchThreshold?: number;
    
    /** Placeholder text when input is empty */
    placeholder?: string;
    
    /** Text to show when searching */
    loadingText?: string;
    
    /** Text to show when no results found */
    noResultsText?: string;
}

export interface Field {
    id: string;
    label: string;
    type: FieldType;
    operators?: string[];
    values?: Record<string, string>; // For select type (static or cached values)
    /** Live search configuration for select fields */
    search?: SearchConfig;
}

/**
 * API response format for field configuration.
 * Supports valuesEndpoint and searchEndpoint for automatic wiring.
 */
export interface APIFieldConfig {
    id: string;
    label: string;
    type: FieldType;
    operators?: string[];
    /** Static values for select fields */
    values?: Record<string, string>;
    /** Endpoint to fetch values once on load (for medium-sized lists) */
    valuesEndpoint?: string;
    /** Search configuration for live search */
    search?: {
        /** Endpoint for live search (will be auto-wired to onSearch) */
        endpoint?: string;
        /** Or provide a custom onSearch callback */
        onSearch?: (term: string, fieldId: string) => Promise<Record<string, string>>;
        /** Endpoint to resolve label for a single key */
        resolveLabelEndpoint?: string;
        mode?: 'local' | 'remote' | 'hybrid';
        minChars?: number;
        debounceMs?: number;
        cache?: boolean | number;
        hybridAlwaysFetch?: boolean;
        hybridFetchThreshold?: number;
        placeholder?: string;
        loadingText?: string;
        noResultsText?: string;
    };
}

/**
 * Options for loadFieldsFromAPI
 */
export interface LoadFieldsOptions {
    /** Custom mapper function to transform API response to Field format */
    mapper?: (apiField: any) => APIFieldConfig;
    /** Base URL prefix for relative endpoints */
    baseUrl?: string;
}

export interface Rule {
    id: string;
    field: string;
    operator: string;
    value: any;
}

export interface RuleGroup {
    id: string;
    condition: 'AND' | 'OR';
    rules: (Rule | RuleGroup)[];
    not?: boolean;
}

export type Operator = {
    label: string;
    cardinality: number; // 0 for none, 1 for single, 2 for between
    input?: string; // Input type override
}

export interface Translations {
    addRule: string;
    addGroup: string;
    deleteRule: string;
    deleteGroup: string;
    logicalOperators: {
        [key: string]: string;
    };
    operators: {
        [key: string]: string;
    };
}

export const DEFAULT_TRANSLATIONS: Translations = {
    addRule: '+ Rule',
    addGroup: '+ Group',
    deleteRule: '×',
    deleteGroup: 'Delete',
    logicalOperators: {
        AND: 'AND',
        OR: 'OR'
    },
    operators: {
        equal: 'Equal',
        not_equal: 'Not Equal',
        in_list: 'In List',
        not_in_list: 'Not In List',
        contains: 'Contains',
        not_contains: 'Not Contains',
        starts_with: 'Starts With',
        ends_with: 'Ends With',
        is_empty: 'Is Empty',
        is_not_empty: 'Is Not Empty',
        is_null: 'Is Null',
        is_not_null: 'Is Not Null',
        less: 'Less',
        less_or_equal: 'Less or Equal',
        greater: 'Greater',
        greater_or_equal: 'Greater or Equal',
        between: 'Between',
        not_between: 'Not Between'
    }
};

export const DEFAULT_OPERATORS: Record<string, Operator> = {
    equal: { label: 'Equal', cardinality: 1 },
    not_equal: { label: 'Not Equal', cardinality: 1 },
    contains: { label: 'Contains', cardinality: 1 },
    not_contains: { label: 'Not Contains', cardinality: 1 },
    starts_with: { label: 'Starts With', cardinality: 1 },
    ends_with: { label: 'Ends With', cardinality: 1 },
    is_empty: { label: 'Is Empty', cardinality: 0 },
    is_not_empty: { label: 'Is Not Empty', cardinality: 0 },
    greater: { label: 'Greater', cardinality: 1 },
    less: { label: 'Less', cardinality: 1 },
    between: { label: 'Between', cardinality: 2 },
    in_array: { label: 'In List', cardinality: 1 },
    not_in_array: { label: 'Not In List', cardinality: 1 }
};

export const OPERATORS_BY_TYPE: Record<FieldType, string[]> = {
    string: ['equal', 'not_equal', 'contains', 'not_contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty', 'in_array', 'not_in_array'],
    number: ['equal', 'not_equal', 'greater', 'less', 'between', 'is_empty', 'is_not_empty', 'in_array', 'not_in_array'],
    boolean: ['equal'],
    date: ['equal', 'not_equal', 'greater', 'less', 'between', 'is_empty', 'is_not_empty', 'in_array', 'not_in_array'],
    select: ['equal', 'not_equal', 'in_array', 'not_in_array', 'is_empty', 'is_not_empty']
};
